import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { emailTemplatesAPI } from '../utils/api';
import { EmailEventoInfo, EmailTemplate } from '../types';
import { usePermissions } from '../hooks/usePermissions';
import toast from 'react-hot-toast';
import { EnvelopeIcon, ChevronDownIcon, ChevronRightIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

interface EditState {
  oggetto: string;
  corpo_html: string;
  attivo: boolean;
}

const EmailTemplates: React.FC = () => {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('email_templates:edit') || hasPermission('email_templates:create');
  const canDelete = hasPermission('email_templates:delete');

  const [expandedEvento, setExpandedEvento] = useState<string | null>(null);
  const [editState, setEditState] = useState<EditState | null>(null);

  const { data: eventsData, isLoading } = useQuery({
    queryKey: ['email-template-events'],
    queryFn: async () => (await emailTemplatesAPI.getEvents()).data,
  });

  const { data: templatesData } = useQuery({
    queryKey: ['email-templates'],
    queryFn: async () => (await emailTemplatesAPI.getTemplates()).data,
  });

  const events: EmailEventoInfo[] = eventsData || [];
  const templates: EmailTemplate[] = templatesData || [];
  const templateIdByEvento = new Map(templates.map(t => [t.evento, t.id]));

  const saveMutation = useMutation({
    mutationFn: (data: { evento: string; oggetto: string; corpo_html: string; attivo: boolean }) =>
      emailTemplatesAPI.createTemplate(data),
    onSuccess: () => {
      toast.success('Template salvato');
      queryClient.invalidateQueries({ queryKey: ['email-template-events'] });
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore durante il salvataggio');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (templateId: string) => emailTemplatesAPI.deleteTemplate(templateId),
    onSuccess: () => {
      toast.success('Template ripristinato al contenuto di default');
      queryClient.invalidateQueries({ queryKey: ['email-template-events'] });
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      setExpandedEvento(null);
      setEditState(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore durante il ripristino');
    },
  });

  const toggleExpand = (evento: EmailEventoInfo) => {
    if (expandedEvento === evento.evento) {
      setExpandedEvento(null);
      setEditState(null);
    } else {
      setExpandedEvento(evento.evento);
      setEditState({
        oggetto: evento.oggetto,
        corpo_html: evento.corpo_html,
        attivo: evento.attivo,
      });
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Template Email</h1>
        <p className="text-sm text-gray-600 mt-0.5">
          Personalizza le email inviate per ogni evento applicativo. Senza personalizzazione viene usato il contenuto di default.
        </p>
      </div>

      {isLoading ? (
        <div className="p-8 text-center text-gray-500">Caricamento...</div>
      ) : (
        <div className="space-y-3">
          {events.map((evento) => {
            const isExpanded = expandedEvento === evento.evento;
            return (
              <div key={evento.evento} className="bg-white shadow rounded-lg overflow-hidden">
                {/* Header evento */}
                <button
                  onClick={() => toggleExpand(evento)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 text-left"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{evento.label}</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          evento.personalizzato ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {evento.personalizzato ? 'Personalizzato' : 'Default'}
                        </span>
                        {!evento.attivo && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            Disattivato
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">{evento.descrizione}</p>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  ) : (
                    <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  )}
                </button>

                {/* Editor evento */}
                {isExpanded && editState && (
                  <div className="border-t border-gray-200 px-4 py-4 space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Oggetto</label>
                      <input
                        type="text"
                        value={editState.oggetto}
                        onChange={(e) => setEditState({ ...editState, oggetto: e.target.value })}
                        disabled={!canEdit}
                        className="block w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Corpo email (HTML)</label>
                      <textarea
                        value={editState.corpo_html}
                        onChange={(e) => setEditState({ ...editState, corpo_html: e.target.value })}
                        rows={10}
                        disabled={!canEdit}
                        className="block w-full px-3 py-1.5 text-sm font-mono border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50"
                      />
                    </div>

                    {/* Variabili disponibili */}
                    <div>
                      <span className="block text-xs font-medium text-gray-500 mb-1">Variabili disponibili:</span>
                      <div className="flex flex-wrap gap-1.5">
                        {evento.variabili.map((v) => (
                          <code key={v} className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded border border-gray-200">
                            {`{{ ${v} }}`}
                          </code>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-1">
                      <label className="flex items-center text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={editState.attivo}
                          onChange={(e) => setEditState({ ...editState, attivo: e.target.checked })}
                          disabled={!canEdit}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mr-2"
                        />
                        Invio attivo per questo evento
                      </label>

                      <div className="flex gap-2">
                        {evento.personalizzato && canDelete && (
                          <button
                            onClick={() => {
                              const id = templateIdByEvento.get(evento.evento);
                              if (id) deleteMutation.mutate(id);
                            }}
                            disabled={deleteMutation.isPending}
                            className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                          >
                            <ArrowPathIcon className="h-4 w-4 mr-1.5" />
                            Ripristina default
                          </button>
                        )}
                        {canEdit && (
                          <button
                            onClick={() => saveMutation.mutate({ evento: evento.evento, ...editState })}
                            disabled={saveMutation.isPending}
                            className="px-4 py-1.5 text-sm border border-transparent rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                          >
                            {saveMutation.isPending ? 'Salvataggio...' : 'Salva template'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EmailTemplates;
